import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useCallback, useMemo, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { BackButton, FloatingContainer, Screen, ZStack } from '~/commonComponents';
import { LeaveInterviewCreationPopup } from '~/commonComponents/popups/LeaveInterviewCreationPopup';
import { SectionCard } from '~/components/ShiftRequestRedesign/common';
import { ReadOnlyDatePill } from '~/components/ShiftRequestRedesign/EditShift/ReadOnlyDatePill';
import { PreferredSpecialtyField } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/fields/PreferredSpecialtyField';
import { ProfessionalPayField } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/fields/ProfessionalPayField';
import { TimeFieldsWrapper } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/fields/TimeFieldsWrapper';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { useIsParentOfficeSelector, useNavigationDetector } from '~/hooks';
import { useUserSelector } from '~/hooks/useUserSelector';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import {
  createOnsiteInterviewFormRulesV2,
  TOnsiteInterviewFormData,
} from '../components/OnsiteInterviewForm/FormRule';
import { useOnsiteInterviewFormSubmitV2 } from '../components/OnsiteInterviewForm/useOnsiteInterviewFormSubmitV2';

interface IEditOnsiteInterviewPageV2Props {
  defaultValues: TOnsiteInterviewFormData;
  jobTitle: string;
  /** Office id (parent-office context) or office name (private practice). */
  office: string;
  /** Profession code (e.g. "RDH") — resolved to display name for the specialty dropdown. */
  profession: string;
  onSubmit: (formData: TOnsiteInterviewFormData) => void;
  onBack: () => void;
}

const EdtBadge = () => (
  <Box
    style={{
      backgroundColor: colors.neutral[400],
      borderRadius: 10,
      paddingInline: 6,
      height: 15,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Typography size="caption_sm" bold color="#fff">
      EDT
    </Typography>
  </Box>
);

export const EditOnsiteInterviewPageV2 = ({
  defaultValues,
  jobTitle,
  office,
  profession,
  onSubmit,
  onBack,
}: IEditOnsiteInterviewPageV2Props) => {
  const timeLimitInMinutes = RemoteConfigUtils.getOnsiteInterviewThresholdInMinutes();
  const timeLimitInHours = Math.round(timeLimitInMinutes / 60);

  const schema = useMemo(
    () => createOnsiteInterviewFormRulesV2(timeLimitInMinutes),
    [timeLimitInMinutes],
  );

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    setValue,
  } = useForm<TOnsiteInterviewFormData>({
    defaultValues,
    mode: 'all',
    resolver: zodResolver(schema) as any,
  });

  const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState(false);

  const { allowNavigation, clearPendingNavigation, navigateToPending } = useNavigationDetector({
    when: isDirty,
    onNavigationAttempt: () => setShowUnsavedChangesPopup(true),
  });

  const {
    bannerRef,
    exceedsLimitMessage,
    fieldErrors,
    markAttemptedSubmit,
    showExceedsLimitBanner,
    showMissingRequiredBanner,
  } = useOnsiteInterviewFormSubmitV2({ control, timeLimitInMinutes });

  const { field: specialtyField } = useController({ control, name: 'specialty' });
  const { field: arrivalField } = useController({ control, name: 'hoursArrival' });
  const { field: departureField } = useController({ control, name: 'hoursDeparture' });
  const { field: rateField } = useController({ control, name: 'hourlyRate' });

  const isButtonDisabled = !isDirty || !isValid;

  const handleSavePressed = useCallback(() => {
    markAttemptedSubmit();
    if (isButtonDisabled) return;
    handleSubmit((formData: TOnsiteInterviewFormData) => {
      allowNavigation();
      onSubmit(formData);
    })();
  }, [allowNavigation, handleSubmit, isButtonDisabled, markAttemptedSubmit, onSubmit]);

  const handleBackPressed = useCallback(() => {
    if (isDirty) {
      setShowUnsavedChangesPopup(true);
      return;
    }
    allowNavigation();
    onBack();
  }, [allowNavigation, isDirty, onBack]);

  const isParentOffice = useIsParentOfficeSelector();
  const currentUser = useUserSelector();

  const professionDisplayName = useMemo(() => {
    if (!profession) return '';
    return (
      UserProfession.getName(profession) ||
      UserSubProfession.getName(profession) ||
      profession
    );
  }, [profession]);

  const rateNumericValue = useMemo(() => {
    if (rateField.value == null || rateField.value === '') return null;
    const parsed = parseFloat(rateField.value);
    return Number.isNaN(parsed) ? null : parsed;
  }, [rateField.value]);

  const handleRateChange = useCallback(
    (v: number) => {
      rateField.onChange(v != null ? `${v}.00` : '');
    },
    [rateField],
  );

  const effectiveOfficeId = isParentOffice ? office : (currentUser?.id ?? '');

  const interviewDate = defaultValues.dates[0];

  return (
    <Screen minWidth={768} paddingInline={30} paddingTop={16} paddingBottom={16}>
      <FloatingContainer
        justifyContent="flex-start"
        maxWidth={1120}
        borderRadius={0}
        margin={0}
        minWidth={0}
        width="100%"
        alignSelf="center"
      >
        <Row align="center" style={{ marginTop: 60, position: 'relative' }}>
          <ZStack left={20}>
            <BackButton onClick={handleBackPressed} />
          </ZStack>
          <Typography variant="h5" textAlign="center" style={{ flex: 1 }}>
            Edit Onsite Interview
          </Typography>
        </Row>

        <Column style={{ paddingInline: 28, paddingTop: 8, paddingBottom: 60 }}>
          {(showMissingRequiredBanner || showExceedsLimitBanner) && (
            <Column style={{ marginTop: 20 }}>
              {showMissingRequiredBanner && (
                <div ref={bannerRef} role="alert" aria-live="polite" data-testid="form-error-banner">
                  <Alert
                    severity="error"
                    style={{ border: 'none', backgroundColor: 'transparent', paddingInline: 0 }}
                    description="Oops! It looks like a few required fields were left empty."
                  />
                </div>
              )}
              {showExceedsLimitBanner && (
                <div
                  ref={showMissingRequiredBanner ? undefined : bannerRef}
                  role="alert"
                  aria-live="polite"
                  data-testid="form-error-banner-exceeds-limit"
                >
                  <Alert
                    severity="error"
                    style={{ border: 'none', backgroundColor: 'transparent', paddingInline: 0 }}
                    description={exceedsLimitMessage}
                  />
                </div>
              )}
            </Column>
          )}

          <Row
            style={{ marginTop: 32, gap: 24, flexWrap: 'wrap' }}
          >
            <Column style={{ flex: 1, minWidth: 430 }}>
              <SectionCard title="Interview Details" testId="edit-interview-details-card">
                {isParentOffice && (
                  <Column
                    style={{ gap: 2, marginBottom: 20 }}
                    data-testid="office-field"
                    data-cy="office-field"
                  >
                    <Typography variant="subtitle1">
                      Office:
                    </Typography>
                    <Typography variant="body1">{office}</Typography>
                  </Column>
                )}

                <Column
                  style={{ gap: 2, marginBottom: 20 }}
                  data-testid="job-title-field"
                  data-cy="job-title-field"
                >
                  <Typography variant="subtitle1">
                    Job Title:
                  </Typography>
                  <Typography variant="body1">{jobTitle}</Typography>
                </Column>

                <PreferredSpecialtyField
                  value={specialtyField.value}
                  onChange={specialtyField.onChange}
                  professionType={professionDisplayName}
                  hasError={fieldErrors.specialty}
                />

                <Typography size="heading_3" bold style={{ marginTop: 24 }}>
                  Hours &amp; Pay
                </Typography>
                <Box
                  style={{
                    height: 1.5,
                    backgroundColor: colors.neutral[100],
                    marginTop: 10,
                    marginBottom: 18,
                  }}
                />

                <Column style={{ gap: 2 }}>
                  <TimeFieldsWrapper
                    arrivalValue={arrivalField.value ?? ''}
                    onArrivalChange={(v) => {
                      arrivalField.onChange(v);
                      setValue('hoursArrival', v, { shouldValidate: true, shouldDirty: true });
                    }}
                    departureValue={departureField.value ?? ''}
                    onDepartureChange={(v) => {
                      departureField.onChange(v);
                      setValue('hoursDeparture', v, { shouldValidate: true, shouldDirty: true });
                    }}
                    labelRight={<EdtBadge />}
                    hasArrivalError={fieldErrors.hoursArrival}
                    hasDepartureError={fieldErrors.hoursDeparture}
                    helperText={null}
                  />
                  <Typography variant="body1" color={colors.neutral[500]}>
                    Onsite interviews can run up to {timeLimitInHours} hours, giving you the time
                    you need to evaluate skills and overall fit.
                  </Typography>
                </Column>

                <Box style={{ marginTop: 20 }}>
                  <ProfessionalPayField
                    value={rateNumericValue}
                    onChange={handleRateChange}
                    professionType={professionDisplayName}
                    officeId={effectiveOfficeId}
                    hasError={fieldErrors.hourlyRate}
                  />
                </Box>
              </SectionCard>
            </Column>

            <Column style={{ flex: 1, minWidth: 430 }}>
              <Box
                data-testid="edit-interview-date-card"
                data-cy="edit-interview-date-card"
                style={{
                  backgroundColor: colors.shades[0],
                  borderRadius: 8,
                  height: 80,
                  paddingInline: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: `1.5px solid ${colors.neutral[100]}`,
                }}
              >
                <Row align="center" style={{ gap: 6 }}>
                  <Typography variant="h6" color={colors.neutral[600]}>
                    Date
                  </Typography>
                  <Typography color={colors.neutral[500]} style={{ marginTop: 4 }}>
                    (Read-Only)
                  </Typography>
                </Row>
                {interviewDate && <ReadOnlyDatePill date={interviewDate} />}
              </Box>
            </Column>
          </Row>

          <Button
            data-testid="save-changes-button"
            data-cy="save-changes-button"
            disabled={!isDirty}
            onClick={handleSavePressed}
            style={{
              width: '100%',
              maxWidth: 296,
              margin: '40px auto 0',
            }}
          >
            Save Changes
          </Button>
        </Column>
      </FloatingContainer>

      <LeaveInterviewCreationPopup
        isOpen={showUnsavedChangesPopup}
        onDiscard={() => {
          setShowUnsavedChangesPopup(false);
          allowNavigation();
          navigateToPending();
        }}
        onKeepEditing={() => {
          setShowUnsavedChangesPopup(false);
          clearPendingNavigation();
        }}
      />
    </Screen>
  );
};
